// The main entry point for the application. Its sole responsibility is to
// initialize all the different modules of the application in the correct order.

// --- Core Module Imports ---
import { showView } from './core/views'
import * as $store from './store'

// --- Feature Module Initializers ---
import { initializeAuth } from './auth/auth'
import { initAccountOnAppLoad } from './account/init'
import { createBranchSelector } from './components/branch-selector'
import { renderAdminPanelHTML, navigateToAdminPanel } from './admin/admin-panel' // Unified admin panel (User Management)
import { initializeUploadListeners } from './uploads/listeners'
import { initializeDataHubListeners } from './data-hub/listeners'
import { initializeModalListeners } from './data-hub/modals'
import { initializeAnalysisNavigation } from './analysis/navigation'
import { renderDynamicSidebar } from './core/sidebar'
import { viewCompiledAnalysis } from './analysis/actions'
import { initBranchScopeSelector, updateBranchScopeSelectorVisibility } from './analysis/branch-selector'
import { initializeBrandManagement } from './brands/brand-management' // <-- ADD THIS
/**
 * Main application initializer.
 * This function orchestrates the setup of all modules.
 */
async function initializeApp(): Promise<void> {
  console.log('Initialytics Application...')

  // 1. Core application setup
  initializeAuth()

  // 2. Initialize account context (branch-centric migration requirement)
  initAccountOnAppLoad()

  // 2.5. Create branch selector (will auto-hide if only 1 branch)
  createBranchSelector()

  // 2.6. Fetch and cache plan limits (T093: Plan Tiering Integration)
  try {
    await $store.fetchAndCachePlanLimits()
    console.log('Plan limits fetched and cached successfully')
  } catch (error) {
    console.error('Failed to fetch plan limits on app load:', error)
    // Continue with default limits from store
  }

  // 3. Render dynamic UI components
  renderDynamicSidebar('new-analysis-sidebar')
  renderAdminPanelHTML() // Inject unified admin panel (User Management) HTML structure

  // 4. Initialize feature modules and their event listeners
  initializeUploadListeners()
  initializeDataHubListeners()
  initializeModalListeners()
  initializeAnalysisNavigation()
  initBranchScopeSelector() // Initialize branch scope selector for multi-branch query control
  updateBranchScopeSelectorVisibility() // Show/hide based on branch count
  initializeBrandManagement()

  // 5. Initialize top-level navigation buttons
  document.getElementById('goto-upload-data-btn')?.addEventListener('click', () => showView('sales-dashboard'))
  document.getElementById('goto-view-data-btn')?.addEventListener('click', () => viewCompiledAnalysis('analysis'))
  document.getElementById('goto-premium-analysis-btn')?.addEventListener('click', () => viewCompiledAnalysis('premium-analysis'))
  document.getElementById('back-to-main-menu-from-data-hub-btn')?.addEventListener('click', () => showView('main-menu'))

  // Wire up user management (admin panel) navigation button
  // Note: This is also wired up in orchestrator.ts, but keeping for redundancy
  document.getElementById('premium-goto-user-management-btn')?.addEventListener('click', () => navigateToAdminPanel())
}

// --- Run the Application ---
initializeApp()
